/**
 * Standalone Text Field Validator
 * No Angular Forms dependency - works independently
 * Can be used with any form library or plain validation
 */

export interface ValidationResult {
  isValid: boolean;
  errors?: string[];
}

export class TextValidator {
  /**
   * Validate text field independently
   * @param value - The value to validate
   * @param fieldType - 'phone' | 'name' | 'text'
   * @param minLength - Minimum characters
   * @param maxLength - Maximum characters
   * @returns ValidationResult with isValid and errors array
   */
  static validate(
    value: string | null | undefined,
    fieldType: 'email' | 'phone' | 'name' | 'text' = 'text',
    minLength: number = 1,
    maxLength: number = 100
  ): ValidationResult {
    const errors: string[] = [];
    const cleanValue = value?.toString().trim() || '';

    // Blank check
    if (!cleanValue) {
      errors.push('Field cannot be blank');
      return { isValid: false, errors };
    }

    // Only zeros check
    if (/^0+$/.test(cleanValue)) {
      errors.push('Field cannot contain only zeros');
      return { isValid: false, errors };
    }

    // "000" pattern check
    if (cleanValue.includes('000')) {
      errors.push('Field contains invalid pattern');
      return { isValid: false, errors };
    }

    switch (fieldType) {
      case 'phone':
        return this.validatePhone(cleanValue, minLength, maxLength);
      case 'name':
        return this.validateName(cleanValue, minLength, maxLength);
      case 'email':
        return this.validateEmail(cleanValue, minLength, maxLength);
      default:
        return this.validateText(cleanValue, minLength, maxLength);
    }
  }

  private static validatePhone(
    value: string,
    minLength: number,
    maxLength: number
  ): ValidationResult {
    const errors: string[] = [];

    // Remove formatting characters
    const cleaned = value.replace(/[\s\-()+]/g, '');

    // Check if only digits
    if (!/^\d+$/.test(cleaned)) {
      errors.push('Phone must contain only digits');
      return { isValid: false, errors };
    }

    // Check length
    if (cleaned.length < minLength) {
      errors.push(`Phone must be at least ${minLength} digits`);
      return { isValid: false, errors };
    }

    if (cleaned.length > maxLength) {
      errors.push(`Phone cannot exceed ${maxLength} digits`);
      return { isValid: false, errors };
    }

    // Check for repeating digits
    if (/(\d)\1{5,}/.test(cleaned)) {
      errors.push('Phone has too many repeating digits');
      return { isValid: false, errors };
    }

    return { isValid: true };
  }

  private static validateName(
    value: string,
    minLength: number,
    maxLength: number
  ): ValidationResult {
    const errors: string[] = [];

    // Check valid characters
    if (!/^[a-zA-Z\s\-']+$/.test(value)) {
      errors.push('Name contains invalid characters');
      return { isValid: false, errors };
    }

    if (/^(.)\1{2,}$/.test(value)) {
      errors.push('Field cannot contain repeating characters only');
      return { isValid: false, errors };
    }
    // Check length
    if (value.length < minLength) {
      errors.push(`Name must be at least ${minLength} characters`);
      return { isValid: false, errors };
    }

    if (value.length > maxLength) {
      errors.push(`Name cannot exceed ${maxLength} characters`);
      return { isValid: false, errors };
    }

    // Check for multiple spaces
    if (/\s{2,}/.test(value)) {
      errors.push('Name cannot have multiple consecutive spaces');
      return { isValid: false, errors };
    }

    return { isValid: true };
  }

  private static validateText(
    value: string,
    minLength: number,
    maxLength: number
  ): ValidationResult {
    const errors: string[] = [];

    if (value.length < minLength) {
      errors.push(`Text must be at least ${minLength} characters`);
      return { isValid: false, errors };
    }

    if (value.length > maxLength) {
      errors.push(`Text cannot exceed ${maxLength} characters`);
      return { isValid: false, errors };
    }

    return { isValid: true };
  }

  private static validateEmail(
    value: string,
    minLength: number,
    maxLength: number
  ): ValidationResult {
    const errors: string[] = [];

    if (value.length < minLength) {
      errors.push(`Email must be at least ${minLength} characters`);
      return { isValid: false, errors };
    }

    if (value.length > maxLength) {
      errors.push(`Email cannot exceed ${maxLength} characters`);
      return { isValid: false, errors };
    }

    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[A-Za-z]{2,}$/.test(value)) {
      errors.push('Please enter a valid email address');
      return { isValid: false, errors };
    }

    return { isValid: true };
  }

  /**
   * Check if a single error condition exists
   * Useful for quick checks
   */
  static isBlank(value: string | null | undefined): boolean {
    return !value?.toString().trim();
  }

  static isOnlyZeros(value: string | null | undefined): boolean {
    const cleanValue = value?.toString().trim() || '';
    return /^0+$/.test(cleanValue);
  }

  static contains000(value: string | null | undefined): boolean {
    return value?.toString().includes('000') || false;
  }

  static isValidPhone(value: string, minLength: number = 7, maxLength: number = 15): boolean {
    const result = this.validate(value, 'phone', minLength, maxLength);
    return result.isValid;
  }

  static isValidName(value: string, minLength: number = 2, maxLength: number = 50): boolean {
    const result = this.validate(value, 'name', minLength, maxLength);
    return result.isValid;
  }

  static isValidText(value: string, minLength: number = 1, maxLength: number = 100): boolean {
    const result = this.validate(value, 'text', minLength, maxLength);
    return result.isValid;
  }

  static isValidEmail(value: string, minLength: number = 6, maxLength: number = 254): boolean {
    const result = this.validate(value, 'text', minLength, maxLength);
    return result.isValid;
  }
}
